import styled from "styled-components";
import {
  fontCondensed,
  fontDisplay,
  fontUi,
  viewMoreLink,
} from "../../../../theme/typography";

export const StyledPage = styled.div`
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  padding: 4px 0 48px;
  color: ${({ theme }) => theme.text};
  box-sizing: border-box;

  @media only screen and (max-width: 768px) {
    padding-bottom: 28px;
  }
`;

export const StyledBack = styled.a`
  ${viewMoreLink};
  margin-bottom: 12px;
`;

export const StyledHeader = styled.section`
  position: relative;
  min-height: 156px;
  overflow: hidden;
  padding: 28px 32px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.cardRadius};
  background: radial-gradient(
      circle at 92% 10%,
      rgba(131, 245, 173, 0.2),
      transparent 34%
    ),
    linear-gradient(135deg, ${({ theme }) => theme.header} 0%, #10261a 100%);
  color: #fff;
  box-sizing: border-box;

  &::after {
    content: "";
    position: absolute;
    right: 34px;
    bottom: -80px;
    width: 240px;
    height: 240px;
    border: 1px solid rgba(131, 245, 173, 0.14);
    border-radius: 50%;
  }

  @media only screen and (max-width: 768px) {
    min-height: 0;
    padding: 22px 20px;
  }
`;

export const StyledEyebrow = styled.span`
  ${fontUi};
  display: block;
  margin-bottom: 6px;
  color: ${({ theme }) => theme.headerAccent};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

export const StyledTitle = styled.h2`
  ${fontDisplay};
  position: relative;
  z-index: 1;
  margin: 0;
  font-size: clamp(28px, 4vw, 42px);
  line-height: 1.05;
  letter-spacing: -0.035em;
`;

export const StyledClub = styled.div`
  ${fontUi};
  position: relative;
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 7px 10px;
  margin-top: 18px;
  color: rgba(255, 255, 255, 0.68);
  font-size: 12px;

  > span {
    color: ${({ theme }) => theme.headerAccent};
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  strong {
    color: #fff;
    font-size: 14px;
  }

  small {
    padding-left: 10px;
    border-left: 1px solid rgba(255, 255, 255, 0.22);
    font-size: 12px;
  }
`;

export const StyledTotals = styled.section`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin: 12px 0;

  > div {
    min-height: 90px;
    padding: 18px 20px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 8px;
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: ${({ theme }) => theme.cardRadius};
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04);
    box-sizing: border-box;
  }

  strong {
    ${fontDisplay};
    font-size: 24px;
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }

  span {
    ${fontUi};
    color: ${({ theme }) => theme.textFaint};
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  @media only screen and (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    > div {
      min-height: 82px;
      padding: 15px 16px;
    }
  }
`;

export const StyledHistory = styled.section`
  overflow: hidden;
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.cardRadius};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04);
`;

export const StyledHistoryHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.borderSubtle};

  span,
  small {
    ${fontUi};
    color: ${({ theme }) => theme.textFaint};
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
  }

  h3 {
    ${fontDisplay};
    margin: 3px 0 0;
    font-size: 21px;
    letter-spacing: -0.02em;
  }

  small {
    padding: 7px 10px;
    background: ${({ theme }) => theme.hover};
    border-radius: ${({ theme }) => theme.controlRadius};
  }
`;

export const StyledTableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
`;

export const StyledTable = styled.table`
  width: 100%;
  min-width: 650px;
  border-collapse: collapse;
  ${fontUi};
  font-size: 13px;

  th,
  td {
    padding: 12px 16px;
    text-align: center;
    border-bottom: 1px solid ${({ theme }) => theme.borderSubtle};
    font-variant-numeric: tabular-nums;
  }

  th:first-child,
  td:first-child,
  th:nth-child(2),
  td:nth-child(2) {
    text-align: left;
  }

  th {
    color: ${({ theme }) => theme.textFaint};
    background: ${({ theme }) => theme.surfaceAlt};
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }

  tbody tr:hover td {
    background: ${({ theme }) => theme.hover};
  }

  td:first-child {
    ${fontCondensed};
    font-weight: 600;
  }

  em {
    ${fontUi};
    display: inline-block;
    margin-left: 8px;
    padding: 3px 6px;
    color: ${({ theme }) =>
      theme.isDark ? theme.headerAccent : theme.success};
    background: ${({ theme }) => theme.accentSoft};
    border-radius: 4px;
    font-size: 9px;
    font-style: normal;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
`;

export const StyledRatingBadge = styled.span`
  ${fontUi};
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 4px 9px;
  color: ${({ $color, theme }) => $color || theme.textMuted};
  background: ${({ $color, theme }) => ($color ? `${$color}18` : theme.hover)};
  border: 1px solid
    ${({ $color, theme }) => ($color ? `${$color}42` : theme.border)};
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  box-sizing: border-box;
`;

export const StyledEmpty = styled.p`
  ${fontUi};
  width: 100%;
  margin: 0;
  padding: 40px 16px;
  text-align: center;
  color: ${({ theme }) => theme.textMuted};
  box-sizing: border-box;
`;
